import { observer } from 'mobx-react';
import { SyntheticEvent } from 'react';

import { useFormikContext } from 'formik';
import { ChoiceOption } from 'types/common';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

type ChipSelectorOption = string | ChoiceOption;

type OwnedAutocompleteProps =
  | 'options'
  | 'value'
  | 'onChange'
  | 'renderInput'
  | 'multiple'
  | 'disabled';

interface PropTypes<T extends ChipSelectorOption>
  extends Omit<Partial<AutocompleteProps<T, true, false, false>>, OwnedAutocompleteProps> {
  options: T[];
  value?: T[];
  onChange?: (event: SyntheticEvent, ...args: unknown[]) => void;
  label: string;
  name: string;
  textFieldStyle?: string;
  disabled?: boolean;
  type?: string;
  helperText?: string;
}

const ChipSelector = <T extends ChipSelectorOption>({
  options,
  value,
  onChange,
  label,
  textFieldStyle = '',
  disabled = false,
  name,
  helperText,
  ...props
}: PropTypes<T>) => {
  const { errors, touched, setFieldTouched } = useFormikContext<Record<string, unknown>>();

  const hasError = Boolean(errors[name]) && Boolean(touched[name]);
  const errorMessage = hasError ? String(errors[name]) : '';

  const handleChange = (e: SyntheticEvent, newValue: T[]) => {
    setFieldTouched(name, true, false);
    onChange(e, newValue);
  };

  return (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={option => (typeof option === 'string' ? option : option[1])}
      filterSelectedOptions
      value={value}
      disabled={disabled}
      disableCloseOnSelect
      onChange={handleChange}
      isOptionEqualToValue={(option, v) => option === (v || v.toString())}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          error={hasError}
          helperText={errorMessage || helperText}
          className={textFieldStyle}
        />
      )}
      {...props}
    />
  );
};

export default observer(ChipSelector) as typeof ChipSelector;
